import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { Spin, Alert, Empty, Progress, Typography } from 'antd';

const { Text } = Typography;

interface GanttChartDisplayProps {
  chartData?: any[];
  chartLayout?: any;
  loading: boolean;
  error?: string | null;
}

const GanttChartDisplay: React.FC<GanttChartDisplayProps> = ({ 
  chartData, 
  chartLayout, 
  loading, 
  error 
}) => {
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStep, setCurrentStep] = useState('');

  // Timer for elapsed time during loading
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      const startTime = Date.now();
      setElapsedTime(0);
      setProgress(0);
      setCurrentStep('Preparing chart data...');
      
      // Simulate progress steps for chart generation
      const steps = [
        { step: 'Analyzing BOM hierarchy...', progress: 20, delay: 500 },
        { step: 'Calculating timelines...', progress: 40, delay: 800 },
        { step: 'Creating visualization...', progress: 70, delay: 1000 },
        { step: 'Rendering chart...', progress: 90, delay: 500 },
      ];

      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        if (stepIndex < steps.length && loading) {
          const { step, progress } = steps[stepIndex];
          setCurrentStep(step);
          setProgress(progress);
          stepIndex++;
        }
      }, 1000);

      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      return () => {
        clearInterval(progressInterval);
        clearInterval(interval);
      };
    } else {
      setProgress(0);
      setCurrentStep('');
      setElapsedTime(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Generating chart..." />
        <div style={{ marginTop: 24, maxWidth: 400, margin: '24px auto 0' }}>
          <Progress 
            percent={progress} 
            status="active"
            strokeColor="#1890ff"
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">{currentStep}</Text>
            <Text type="secondary">Elapsed: {formatTime(elapsedTime)}</Text>
          </div>
          {elapsedTime > 30 && (
            <div style={{ marginTop: 8 }}>
              <Text type="warning" style={{ fontSize: '12px' }}>
                Complex data detected - chart generation may take longer than usual
              </Text>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert 
        message="Chart Error" 
        description={error} 
        type="error" 
        showIcon 
        style={{ margin: '20px 0' }} 
      />
    );
  }

  if (!chartData || !chartLayout || chartData.length === 0) {
    return (
      <div style={{ padding: '50px 0', textAlign: 'center' }}>
        <Empty description="No data to display or chart not generated yet. Please upload files and process." />
      </div>
    );
  }

  // Ensure layout height is reasonable, or Plotly might not render well initially
  const dynamicHeight = chartLayout.height || 600;

  // Log chart rendering for debugging
  console.log(`Rendering Gantt chart with ${chartData.length} data series and height ${dynamicHeight}px`);

  return (
    <div style={{ width: '100%' }}>
      <style>
        {`
          /* Enable text selection on Plotly y-axis labels */
          .ytick text, .y axis .tick text, .yaxis .tick text {
            user-select: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
            cursor: text !important;
          }
          
          /* Make sure y-axis labels are selectable */
          .plotly .yaxis .tick text tspan {
            user-select: text !important;
            pointer-events: all !important;
          }
          
          /* Enable selection for all SVG text elements in y-axis area */
          .plotly svg g.yaxis text {
            user-select: text !important;
            -webkit-user-select: text !important;
            cursor: text !important;
          }
        `}
      </style>
      <Plot
        data={chartData}
        layout={{ ...chartLayout, height: dynamicHeight }}
        style={{ width: '100%', minHeight: `${dynamicHeight}px` }}
        config={{ 
          responsive: true, 
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d'],
          displayModeBar: true,
          // Enable text selection and copying for y-axis labels
          staticPlot: false,
          editable: false,
          showTips: true,
          // Enable copying functionality
          toImageButtonOptions: {
            format: 'png',
            filename: 'gantt-chart',
            height: 800,
            width: 1200,
            scale: 1
          }
        }}
        onInitialized={() => {
          console.log("Plotly chart initialized successfully");
          console.log(`Chart data points: ${chartData.reduce((sum, trace) => sum + (trace.x?.length || 0), 0)}`);
          
          // Enable text selection on y-axis labels after chart is rendered
          setTimeout(() => {
            const yaxisTexts = document.querySelectorAll('.plotly .yaxis .tick text, .plotly .ytick text');
            yaxisTexts.forEach((textElement) => {
              const element = textElement as HTMLElement;
              element.style.userSelect = 'text';
              element.style.webkitUserSelect = 'text';
              element.style.cursor = 'text';
              element.style.pointerEvents = 'all';
            });
          }, 500);
        }}
        onError={(err) => {
          console.error("Plotly chart error:", err);
        }}
        onUpdate={() => {
          console.log("Plotly chart updated");
          
          // Re-enable text selection on y-axis labels after chart updates
          setTimeout(() => {
            const yaxisTexts = document.querySelectorAll('.plotly .yaxis .tick text, .plotly .ytick text');
            yaxisTexts.forEach((textElement) => {
              const element = textElement as HTMLElement;
              element.style.userSelect = 'text';
              element.style.webkitUserSelect = 'text';
              element.style.cursor = 'text';
              element.style.pointerEvents = 'all';
            });
          }, 100);
        }}
      />
    </div>
  );
};

export default GanttChartDisplay;